/* /start - the age question, then the create-account form.
   - The question is neutral: nothing on the page mentions an age limit, and the year list runs back 100 years.
   - The decision is made here with core ageDecision; the server checks again.
   - Under 13: a neutral message, nothing is sent anywhere, and this browser shows the same message for 24 hours.
   - The date of birth stays in memory and goes only into the sign-up request. */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { WEB_ROUTES, asLang, type Lang } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link, navigate, useLocation } from '../app/nav';
import { useErrorHandler, focusHeading } from '../app/hooks';
import { signupMemory } from '../app/memory';
import { Alert, Button, PageHeader, PasswordField, SelectField, TextField, Select } from '../app/ui';
import { birthYears, checkDob, looksLikeEmail, MIN_PASSWORD } from '../lib/forms';
import { monthNames } from '../lib/format';
import { api, ApiError } from '../lib/api';
import { isAgeBlocked, setAgeBlock } from '../lib/storage';
import { LANG_OPTIONS, type MessageKey } from '../lib/i18n';
import { signinUrl } from '../lib/routes';
import { beginId } from '../lib/globe-data';
import { Icon } from '../app/icons';

type Stage = { s: 'dob' } | { s: 'blocked' } | { s: 'form'; dob: string; minor: boolean };

/* where the student is in signing up: three steps (the date of birth, the account, the emailed code), drawn as three
   bars and read out as "Step 1 of 3"; from the landing page's globe, the university they're starting with; from the
   planner, what it's for. No digits on the page itself: the date-of-birth step shows no numbers that could read as an
   age limit. */
export const fromPlannerNext = (next: string | null) => (next || '').startsWith(WEB_ROUTES.planner.replace(/\/$/, ''));
export function useSignupStep(n: number, planner?: boolean): ReactNode {
  const { t } = useT();
  const { query } = useLocation();
  const next = query.get('next');
  const fromPlanner = planner ?? fromPlannerNext(next);
  const school = useBeginSchool(next);
  return (
    <>
      <span className="signup-steps" role="img" aria-label={t('auth.step', { n, total: 3 })}>
        {[1, 2, 3].map(i => <i key={i} className={i <= n ? 'on' : undefined} />)}
      </span>
      {school ? <span className="signup-begin"><Icon.target />{t('auth.beginWith', { school })}</span> : fromPlanner && <span>{t('auth.saveList')}</span>}
    </>
  );
}
/* the university a sign-up from the landing page's globe starts with (/planner/#begin=<id> in next), named from the
   globe's own data, which the landing page has already loaded */
export function useBeginSchool(next: string | null): string | null {
  const id = beginId(next);
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    if (!id) { setName(null); return; }
    let live = true;
    import('./landing/globe/schools.json').then(m => {
      const s = (m.default as { schools: Array<{ i: string; n: string }> }).schools.find(x => x.i === id);
      if (live) setName(s ? s.n : null);
    }).catch(() => {});
    return () => { live = false; };
  }, [id]);
  return name;
}
/* the rest of signing up keeps where it's going next (the planner, with the university) */
const withNext = (path: string, next: string | null) => (next ? `${path}?next=${encodeURIComponent(next)}` : path);

export function Start() {
  const [stage, setStage] = useState<Stage>(() => (isAgeBlocked() ? { s: 'blocked' } : { s: 'dob' }));
  const [parts, setParts] = useState({ y: '', m: '', d: '' });
  /* each step has its own heading: move focus to it so screen readers announce the change */
  const mounted = useRef(false);
  useEffect(() => { if (!mounted.current) { mounted.current = true; return; } focusHeading(); }, [stage.s]);

  if (stage.s === 'blocked') return <Blocked />;
  if (stage.s === 'dob') {
    return <DobStep parts={parts} setParts={setParts} onDone={(dob, minor) => setStage({ s: 'form', dob, minor })}
      onBlocked={() => { setAgeBlock(); setStage({ s: 'blocked' }); }} />;
  }
  return <SignupForm dob={stage.dob} minor={stage.minor} onChangeDob={() => setStage({ s: 'dob' })}
    onBlocked={() => { setAgeBlock(); setStage({ s: 'blocked' }); }} />;
}

function Blocked() {
  const { t } = useT();
  return (
    <div className="narrow">
      <PageHeader title={t('start.blocked.title')} />
      <p><Link to="/">{t('common.home')}</Link></p>
    </div>
  );
}

export function DateSelects({ idPrefix, parts, setParts, error }: {
  idPrefix: string; parts: { y: string; m: string; d: string }; setParts: (p: { y: string; m: string; d: string }) => void; error?: boolean;
}) {
  const { t, lang } = useT();
  const months = monthNames(lang);
  const invalid = error ? true : undefined;
  return (
    <div className="dob">
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-day`}>{t('start.dob.day')}</label>
        <Select id={`${idPrefix}-day`} value={parts.d} onChange={d => setParts({ ...parts, d })} placeholder="–" aria-invalid={invalid}
          options={Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))} autoComplete="bday-day" />
      </div>
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-month`}>{t('start.dob.month')}</label>
        <Select id={`${idPrefix}-month`} value={parts.m} onChange={m => setParts({ ...parts, m })} placeholder="–" aria-invalid={invalid}
          options={months.map((name, i) => ({ value: String(i + 1), label: name }))} autoComplete="bday-month" />
      </div>
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-year`}>{t('start.dob.year')}</label>
        <Select id={`${idPrefix}-year`} value={parts.y} onChange={y => setParts({ ...parts, y })} placeholder="–" aria-invalid={invalid}
          options={birthYears().map(y => ({ value: String(y), label: String(y) }))} autoComplete="bday-year" />
      </div>
    </div>
  );
}

function DobStep({ parts, setParts, onDone, onBlocked }: {
  parts: { y: string; m: string; d: string }; setParts: (p: { y: string; m: string; d: string }) => void;
  onDone: (dob: string, minor: boolean) => void; onBlocked: () => void;
}) {
  const { t } = useT();
  const step = useSignupStep(1);
  const next = useLocation().query.get('next');
  const [error, setError] = useState<MessageKey | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = checkDob(parts.y, parts.m, parts.d);
    if (!r.ok) { setError(r.error); return; }
    if (!r.decision.allowed) { onBlocked(); return; }
    onDone(r.dob, r.decision.guardianRequired);
  };
  return (
    <div className="narrow">
      <PageHeader title={t('start.dob.title')} eyebrow={step} />
      <form className="card" noValidate onSubmit={submit} aria-labelledby="page-title">
        <DateSelects idPrefix="dob" parts={parts} setParts={p => { setParts(p); setError(null); }} error={!!error} />
        {error && <div className="mt"><Alert>{t(error)}</Alert></div>}
        <div className="actions"><Button type="submit" block>{t('common.continue')}</Button></div>
      </form>
      <p className="aside">{t('start.haveAccount')} <Link to={next ? signinUrl(next) : WEB_ROUTES.signin}>{t('signin.title')}</Link></p>
    </div>
  );
}

function SignupForm({ dob, minor, onChangeDob, onBlocked }: { dob: string; minor: boolean; onChangeDob: () => void; onBlocked: () => void }) {
  const { t, rich, lang, setLang } = useT();
  const handle = useErrorHandler();
  const { query } = useLocation();
  const planner = fromPlannerNext(query.get('next'));
  const step = useSignupStep(2, planner);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formLang, setFormLang] = useState<Lang>(lang);
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!looksLikeEmail(email)) errs.email = t('v.email');
    if ([...password].length < MIN_PASSWORD) errs.password = t('v.password');
    setErrors(errs);
    if (errs.email || errs.password) { document.getElementById(errs.email ? 'su-email' : 'su-password')?.focus(); return; }
    setBusy(true);
    const body = { email: email.trim(), password, dob, lang: formLang };
    try {
      await api.post('/api/auth/signup', body);
      signupMemory.set({ ...body, minor, planner });
      navigate(withNext(WEB_ROUTES.verifyEmail, query.get('next')));
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'age_blocked') { onBlocked(); return; }
      if (err instanceof ApiError && err.code === 'invalid' && (err.field === 'email' || err.field === 'password')) {
        setErrors({ [err.field]: err.field === 'email' ? t('v.email') : t('v.password') });
        return;
      }
      const msg = handle(err);
      if (msg) setErrors({ form: msg });
    }
  };

  return (
    <div className="narrow">
      <PageHeader title={t('start.form.title')} eyebrow={step} />
      {minor && <div className="mb"><Alert tone="info">{t('start.form.minor')}</Alert></div>}
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        <TextField id="su-email" label={t('common.email')} type="email" autoComplete="email" inputMode="email" ltr
          autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} hint={t('start.form.emailHint')} />
        <PasswordField id="su-password" label={t('common.password')} value={password} onChange={setPassword}
          autoComplete="new-password" showStrength error={errors.password} />
        <SelectField id="su-lang" label={t('start.form.lang')} hint={t('start.form.langHint')} value={formLang} placeholder={false}
          onChange={v => { const l = asLang(v); setFormLang(l); setLang(l); }}
          options={LANG_OPTIONS} />
        {errors.form && <Alert>{errors.form}</Alert>}
        <p className="fine">
          {rich('start.form.agree', {
            terms: <Link to={WEB_ROUTES.terms}>{t('start.form.termsLink')}</Link>,
            privacy: <Link to={WEB_ROUTES.privacy}>{t('start.form.privacyLink')}</Link>,
          })}
        </p>
        <Button type="submit" block busy={busy}>{t('start.form.submit')}</Button>
      </form>
      <p className="aside">
        <button type="button" className="linkbtn" onClick={onChangeDob}>{t('start.form.changeDob')}</button>
      </p>
    </div>
  );
}
